import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../Icon';
import ArchivedToggle from '../ui/ArchivedToggle';
import FilterPopover from '../ui/FilterPopover';
import { setAsoebiOrdersBar, useAsoebiOrdersBar } from '../../hooks/useAsoebiOrdersBar';
import { useRole } from '../../hooks/useRole';
import { ORDER_PAYMENT_OPTIONS, ORDER_STATUS_OPTIONS } from '../../utils/asoebiOrdersList';
import { ASOEBI_ORDER_NEW_PATH } from '../../utils/asoebiOrdersRoute';

const withAll = (all: string, options: ReadonlyArray<{ value: string; label: string }>) => [{ value: '', label: all }, ...options];

/**
 * The Orders list's actions, as on the other list bars: one Filters popover
 * (Status, Payment), the Archived switch, and New order as the one labelled
 * primary button. A viewer (below planner level) gets no New order, and
 * archived orders cannot be added to.
 *
 * Header.tsx renders this in the breadcrumb bar for the 'asoebi-orders' view, on the list address only
 * (an order's own page has its own form and no list actions). It reads only the bar store; the list
 * (AsoebiOrdersList) reads the same store to filter its rows.
 */
export const AsoebiOrdersBar: React.FC = () => {
  const navigate = useNavigate();
  const bar = useAsoebiOrdersBar();
  const { canEdit, canViewFinancials } = useRole();

  return (
    <>
      <FilterPopover
        selects={[
          {
            key: 'status', label: 'Status', options: withAll('All statuses', ORDER_STATUS_OPTIONS), value: bar.status, defaultValue: '',
            onChange: status => setAsoebiOrdersBar({ status }),
          },
          // Payment state is money-derived: not offered to a role that may not see money.
          ...(canViewFinancials ? [{
            key: 'payment', label: 'Payment', options: withAll('Any payment', ORDER_PAYMENT_OPTIONS), value: bar.payment, defaultValue: '',
            onChange: (payment: string) => setAsoebiOrdersBar({ payment }),
          }] : []),
        ]}
      />
      <div className="header-filter-group">
        <ArchivedToggle checked={bar.archived} onChange={archived => setAsoebiOrdersBar({ archived })} />
      </div>
      {canEdit && !bar.archived && (
        <button type="button" className="header-action-btn primary" onClick={() => navigate(ASOEBI_ORDER_NEW_PATH)}>
          <Icon name="plus" size={16} />
          <span className="header-action-btn__label">New order</span>
        </button>
      )}
    </>
  );
};

export default AsoebiOrdersBar;
